/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as React from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import type { CheckboxProps } from "../../../_base/_core/Checkbox/Checkbox.emotion";
import { Checkbox } from "../../../_base/_core/Checkbox/Checkbox.emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};
const labelContainerStyle: Interpolation = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  flex: 1,
};
const labelStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.primary,
  cursor: "pointer",
  flex: 1,
  marginLeft: spacing.ms,
});
const infoIconStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsHigh,
  cursor: "pointer",
  flexShrink: 0,
});
const disabledStyle: Interpolation = {
  cursor: "not-allowed",
  opacity: 0.5,
};

export interface CheckboxInputProps extends Omit<CheckboxProps, "id"> {
  label: string;
  infoIcon?: React.ReactNode;
  id?: string;
  containerCSS?: Interpolation;
  labelCSS?: Interpolation;
}

export const CheckboxInput = React.forwardRef<
  React.ComponentRef<typeof Checkbox>,
  CheckboxInputProps
>(
  (
    {
      label,
      infoIcon,
      id,
      disabled,
      containerCSS = {},
      labelCSS = {},
      ...checkboxProps
    },
    ref,
  ) => {
    const checkboxId = id || React.useId();

    return (
      <div css={[containerStyle, disabled && disabledStyle, containerCSS]}>
        <label css={[labelContainerStyle, disabled && disabledStyle]}>
          <Checkbox
            ref={ref}
            id={checkboxId}
            disabled={disabled}
            {...checkboxProps}
          />
          <Text
            type="body"
            size="m"
            css={[labelStyle, disabled && disabledStyle, labelCSS]}
          >
            {label}
          </Text>
          {infoIcon && (
            <div css={[infoIconStyle, disabled && disabledStyle]}>
              {infoIcon}
            </div>
          )}
        </label>
      </div>
    );
  },
);

CheckboxInput.displayName = "CheckboxInput";
